// Мастер загрузки сумм в период. Сценарий 69.
//
// Три шага, и второй существует ради одного: **ничего не записывается, пока человек не увидел
// отчёт**. Сопоставление фамилий — отдельная операция, которая только читает: «ляжет столько,
// спорных столько, не найдено столько». В книге заказчика фамилии написаны по-разному в разных
// листах, и молчаливая загрузка «как поняли» означала бы деньги, легшие не на того человека.
//
// Спорные строки человек разводит руками, выбрав из кандидатов. Не найденные не грузятся вовсе
// и остаются на экране списком: пропажу иначе замечают на сверке в конце месяца.
//
// ## Почему запись идёт загрузкой пачкой, а не отдельным входом «импорт»
//
// Отдельный вход принимает только человека и сумму, а запись перезаписывает строку ЦЕЛИКОМ.
// Значит повторная заливка книги — а её перезаливают при каждой правке — молча снимала бы
// процент по исполнительному документу, состояние и заметку у всех, кто в периоде уже есть.
// Загрузка пачкой принимает строку целиком, и мы досылаем в ней текущие значения.

import { useState } from 'react';

import { RiArrowLeftLine } from '@remixicon/react';

import type { MatchOut, MatchRowOut, OfficialRowOut, RowIn } from '@/shared/api/endpoints/official';
import { formatKopecks } from '@/shared/money/format';
import { countLabel } from '@/shared/text/plural';
import { TextArea } from '@/shared/ui/text-area';
import * as Button from '@/shared/ui/button';
import { cn } from '@/utils/cn';
import { ErrorAlert } from '@/shared/ui/error-alert';

import { useGrid, useMatchNames, useUpsertRows } from '../queries';

import { разобратьВставкуСОтчётом } from './paste-lines';

export function ImportPanel({ id }: { id: string }) {
  const сопоставление = useMatchNames(id);
  const загрузка = useUpsertRows(id);
  // Сетка уже в кэше: экран её только что запросил. Лишнего обращения к серверу это не стоит.
  const сетка = useGrid(id, {});
  const период = сетка.data?.period ?? null;
  const существующие = сетка.data?.rows ?? [];

  const [текст, setТекст] = useState('');
  const [выбор, setВыбор] = useState<Record<number, string>>({});
  const [исключены, setИсключены] = useState<readonly number[]>([]);
  const [ошибка, setОшибка] = useState<string | null>(null);
  // Что разбор вставки сказал про колонки: показывается над отчётом, пока человек его читает.
  const [разбор, setРазбор] = useState<{
    поШапке: boolean;
    колонкаСуммы: string | null;
    несколькоЧисел: boolean;
  } | null>(null);

  const отчёт = сопоставление.data;
  const итог = загрузка.data;

  if (итог !== undefined) {
    return (
      <div className='flex flex-col gap-3'>
        <h3 className='text-label-lg text-text-strong-950'>Загружено</h3>
        <p className='text-paragraph-sm text-text-strong-950'>
          Легло {countLabel(итог.loaded, ['строка', 'строки', 'строк'])}.
        </p>
        {итог.skipped.length > 0 && (
          <div className='flex flex-col gap-1'>
            <p className='text-paragraph-sm text-text-sub-600'>Не легло {итог.skipped.length}:</p>
            <ul className='ml-4 list-disc text-paragraph-xs text-text-sub-600'>
              {итог.skipped.map((пункт, номер) => (
                <li key={`${пункт.label}-${номер}`}>
                  {пункт.label} — {пункт.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Button.Root
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={() => {
            загрузка.reset();
            сопоставление.reset();
            setТекст('');
            setВыбор({});
            setИсключены([]);
            setРазбор(null);
          }}
        >
          Загрузить ещё
        </Button.Root>
      </div>
    );
  }

  if (отчёт === undefined) {
    return (
      <div className='flex flex-col gap-3'>
        <div className='flex flex-col gap-1'>
          {/* Куда лягут деньги — обязательная часть заголовка: период выбирается в шапке
              экрана, а мастер открывается поверх и её закрывает. */}
          <h3 className='text-label-lg text-text-strong-950'>
            {период === null ? 'Загрузка сумм' : `В период ${период.title}`}
          </h3>
          <p className='text-paragraph-xs text-text-sub-600'>
            Скопируйте таблицу из Excel вместе со строкой шапки: колонки «ФИО» и «Всего…» найдутся
            сами. Без шапки: фамилия в первой колонке, сумма в последней. Ничего не запишется, пока
            вы не увидите отчёт.
          </p>
        </div>
        <TextArea
          id='вставка-сумм'
          label='Фамилии и суммы из книги'
          value={текст}
          onChange={setТекст}
          rows={10}
          monospace
          placeholder={'Старцев А.\t25 934,00\nФеллер А.\t51 869,40\nТарасов А.В.\t55 252,26'}
        />
        {ошибка !== null && <p className='text-paragraph-xs text-warning-base'>{ошибка}</p>}
        <Button.Root
          variant='primary'
          mode='filled'
          size='xsmall'
          onClick={() => {
            const результат = разобратьВставкуСОтчётом(текст);
            if (результат.строки.length === 0) {
              setОшибка(
                'Разобрать нечего: вставьте строки вида «Фамилия И.» и сумма — так, как они копируются из Excel.',
              );
              return;
            }
            setОшибка(null);
            setРазбор({
              поШапке: результат.поШапке,
              колонкаСуммы: результат.колонкаСуммы,
              несколькоЧисел: результат.несколькоЧисел,
            });
            сопоставление.mutate(результат.строки);
          }}
        >
          {сопоставление.isPending ? 'Сопоставляем…' : 'Сопоставить фамилии'}
        </Button.Root>
        {сопоставление.error !== null && <ErrorAlert error={сопоставление.error} />}
      </div>
    );
  }

  const кЗагрузке = собратьЗагрузку(отчёт, выбор, исключены);
  const тела = собратьТела(кЗагрузке, существующие);
  const нерешённые = отчёт.rows.filter(
    (строка) => строка.verdict === 'ambiguous' && выбор[строка.line] === undefined,
  );
  const поверхСуществующих = тела.filter((тело) =>
    существующие.some((строка) => строка.employee_id === тело.employee_id),
  ).length;
  const сУдержанием = тела.filter((тело) => тело.writ_percent != null).length;

  return (
    <div className='flex flex-col gap-3'>
      <button
        type='button'
        onClick={() => {
          сопоставление.reset();
          setВыбор({});
          setИсключены([]);
          setРазбор(null);
        }}
        className='flex items-center gap-1 self-start text-paragraph-xs text-text-sub-600'
      >
        <RiArrowLeftLine className='size-3.5' />К вставке
      </button>

      <div className='flex flex-col gap-1'>
        <h3 className='text-label-lg text-text-strong-950'>Что получится</h3>
        {/* Числа названы ДО записи, а не после: это и есть смысл отдельного шага. */}
        <p className='text-paragraph-sm text-text-strong-950'>
          Ляжет {countLabel(кЗагрузке.length, ['строка', 'строки', 'строк'])} на{' '}
          {formatKopecks(суммаЗагрузки(отчёт, выбор, исключены))}.
        </p>
        <p className='text-paragraph-xs text-text-sub-600'>
          Точных совпадений {отчёт.exact_count}, с оговоркой {отчёт.fuzzy_count}, спорных{' '}
          {отчёт.ambiguous_count}, не найдено {отчёт.not_found_count}.
        </p>
        {разбор !== null && разбор.поШапке && (
          <p className='text-paragraph-xs text-text-sub-600'>
            Колонки найдены по шапке таблицы: фамилии из «ФИО», суммы из «
            {разбор.колонкаСуммы ?? 'Всего'}». Если нужна другая колонка, вернитесь к вставке.
          </p>
        )}
        {разбор !== null && разбор.несколькоЧисел && (
          // Шапки нет, а чисел в строке несколько: взята последняя колонка, и это может быть
          // «Остаток», а не официальная сумма. Сказать надо ДО загрузки, а не на сверке.
          <p className='rounded-lg bg-bg-weak-50 px-3 py-2 text-paragraph-xs text-warning-base'>
            В строках по нескольку чисел, а шапки нет, поэтому взята последняя колонка. Если вам
            нужна «Всего без НДФЛ», а не «Остаток», вернитесь к вставке и скопируйте таблицу вместе
            со строкой шапки.
          </p>
        )}
        {/* Загрузка перезаписывает строку целиком. Проценты, состояния и заметки уже
            заведённых строк сохраняются — иначе повторная заливка книги молча снимала бы
            удержание по исполнительному документу у всех сразу. */}
        {поверхСуществующих > 0 && (
          <p className='text-paragraph-xs text-text-sub-600'>
            Ляжет поверх уже заведённых строк: {поверхСуществующих}. Проценты по исполнительным
            документам, состояния и заметки у них сохранятся
            {сУдержанием > 0 && <> — удержание стоит у {сУдержанием}</>}.
          </p>
        )}
      </div>

      {нерешённые.length > 0 && (
        <p className='rounded-lg bg-bg-weak-50 px-3 py-2 text-paragraph-xs text-warning-base'>
          Спорных строк без решения: {нерешённые.length}. Они не загрузятся, пока вы не выберете
          человека — деньги, легшие не на того, снимаются только сторно.
        </p>
      )}

      <ul className='flex flex-col gap-2'>
        {отчёт.rows.map((строка) => (
          <СтрокаОтчёта
            key={строка.line}
            строка={строка}
            выбран={выбор[строка.line]}
            исключена={исключены.includes(строка.line)}
            onSelect={(employeeId) =>
              setВыбор((прежний) => ({ ...прежний, [строка.line]: employeeId }))
            }
            onToggle={() =>
              setИсключены((прежние) =>
                прежние.includes(строка.line)
                  ? прежние.filter((номер) => номер !== строка.line)
                  : [...прежние, строка.line],
              )
            }
          />
        ))}
      </ul>

      <Button.Root
        variant='primary'
        mode='filled'
        size='xsmall'
        onClick={() => {
          if (кЗагрузке.length === 0) {
            setОшибка('Загружать нечего: ни одна строка не сопоставилась с человеком.');
            return;
          }
          setОшибка(null);
          загрузка.mutate(тела);
        }}
      >
        {загрузка.isPending
          ? 'Загружаем…'
          : `Загрузить ${countLabel(кЗагрузке.length, ['строку', 'строки', 'строк'])}`}
      </Button.Root>
      {ошибка !== null && <p className='text-paragraph-xs text-warning-base'>{ошибка}</p>}
      {загрузка.error !== null && <ErrorAlert error={загрузка.error} />}
    </div>
  );
}

function СтрокаОтчёта({
  строка,
  выбран,
  исключена,
  onSelect,
  onToggle,
}: {
  строка: MatchRowOut;
  выбран: string | undefined;
  исключена: boolean;
  onSelect: (employeeId: string) => void;
  onToggle: () => void;
}) {
  const кандидаты = строка.candidates ?? [];
  const можноОтказаться = строка.verdict !== 'not_found' && строка.amount !== null;
  return (
    <li
      className={cn(
        'flex flex-col gap-1 rounded-lg border border-stroke-soft-200 px-3 py-2',
        исключена && 'opacity-50',
      )}
    >
      <div className='flex items-baseline justify-between gap-2'>
        <span className='truncate text-paragraph-sm text-text-strong-950'>{строка.raw_name}</span>
        <span className='shrink-0 tabular-nums text-paragraph-sm text-text-sub-600'>
          {строка.amount === null ? '—' : formatKopecks(строка.amount)}
        </span>
      </div>
      <span className='text-paragraph-xs text-text-sub-600'>{вердиктСловами(строка)}</span>
      {строка.amount_error !== null && (
        <span className='text-paragraph-xs text-warning-base'>{строка.amount_error}</span>
      )}
      {можноОтказаться && (
        // Сопоставление идёт по фамилии и первой букве имени: «Зорин И.» из книги ляжет на
        // единственного Зорина с именем на «И», даже если в книге имелся в виду другой. Увидеть
        // это может только человек, значит у него должна быть возможность отказаться построчно.
        <button
          type='button'
          onClick={onToggle}
          className='self-start text-paragraph-xs text-text-sub-600 underline underline-offset-2'
        >
          {исключена ? 'Вернуть в загрузку' : 'Не грузить эту строку'}
        </button>
      )}
      {строка.verdict === 'ambiguous' && кандидаты.length > 0 && (
        <div className='flex flex-wrap gap-1 pt-1'>
          {кандидаты.map((кандидат) => (
            <Button.Root
              key={кандидат.employee_id}
              variant={выбран === кандидат.employee_id ? 'primary' : 'neutral'}
              mode={выбран === кандидат.employee_id ? 'filled' : 'stroke'}
              size='xxsmall'
              // Имя с отчеством и состояние («Ванин Николай Петрович · Межвахтовый отдых») не
              // влезают в панель одной строкой: без переноса кнопка уходила за её край.
              className='h-auto min-h-7 max-w-full whitespace-normal py-1 text-left'
              onClick={() => onSelect(кандидат.employee_id)}
            >
              {кандидат.employee_name}
              {кандидат.status_title != null && ` · ${кандидат.status_title}`}
            </Button.Root>
          ))}
        </div>
      )}
    </li>
  );
}

function вердиктСловами(строка: MatchRowOut): string {
  if (строка.verdict === 'exact') {
    return `нашли: ${строка.employee_name ?? ''}`;
  }
  if (строка.verdict === 'fuzzy') {
    return `нашли с оговоркой: ${строка.employee_name ?? ''}${строка.note === null ? '' : ` — ${строка.note}`}`;
  }
  if (строка.verdict === 'ambiguous') {
    // Причину называет сервер: «несколько человек», «имя не совпало», «написана иначе». Одна
    // и та же фраза про «несколько человек» врала бы про две из трёх причин.
    return строка.note ?? 'совпало несколько человек — выберите, на кого лягут деньги';
  }
  return `не нашли${строка.note === null ? '' : ` — ${строка.note}`}; строка не загрузится`;
}

/** Что реально уйдёт на сервер: только строки с человеком, с разобранной суммой и не отменённые. */
export function собратьЗагрузку(
  отчёт: MatchOut,
  выбор: Record<number, string>,
  исключены: readonly number[] = [],
): { employee_id: string; gross_official: number }[] {
  const строки: { employee_id: string; gross_official: number }[] = [];
  for (const строка of отчёт.rows) {
    const человек = выбор[строка.line] ?? строка.employee_id;
    if (человек === null || человек === undefined || строка.amount === null) {
      continue;
    }
    if (строка.verdict === 'not_found' || исключены.includes(строка.line)) {
      continue;
    }
    строки.push({ employee_id: человек, gross_official: строка.amount });
  }
  return строки;
}

/**
 * Тело загрузки: новая сумма поверх ТЕКУЩИХ значений уже заведённой строки.
 *
 * Загрузка перезаписывает строку целиком. Прислав только сумму, мы обнулили бы процент по
 * исполнительному документу, состояние и заметку у каждого, кто в периоде уже есть, — а книгу
 * заказчика перезаливают при каждой правке. У 28 человек это разом снятые удержания приставов.
 */
export function собратьТела(
  загрузка: readonly { employee_id: string; gross_official: number }[],
  существующие: readonly OfficialRowOut[],
): RowIn[] {
  return загрузка.map((пункт) => {
    const прежняя = существующие.find((строка) => строка.employee_id === пункт.employee_id);
    return {
      employee_id: пункт.employee_id,
      gross_official: пункт.gross_official,
      writ_percent: прежняя?.writ_percent ?? null,
      state: прежняя?.state ?? null,
      note: прежняя?.note ?? null,
    };
  });
}

function суммаЗагрузки(
  отчёт: MatchOut,
  выбор: Record<number, string>,
  исключены: readonly number[],
): number {
  return собратьЗагрузку(отчёт, выбор, исключены).reduce(
    (итог, строка) => итог + строка.gross_official,
    0,
  );
}
